import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Plus, Edit, Trash2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Auctions = () => {
  const [auctions, setAuctions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAuction, setEditingAuction] = useState(null);
  const [formData, setFormData] = useState({
    product_id: '',
    starting_price: '',
    current_bid: '',
    min_increment: '',
    end_time: '',
    status: 'active'
  });

  useEffect(() => {
    fetchAuctions();
    fetchProducts();
  }, []);

  const fetchAuctions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auctions`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuctions(data);
      }
    } catch (error) {
      console.error('Failed to fetch auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const auctionData = {
      ...formData,
      starting_price: parseFloat(formData.starting_price),
      current_bid: parseFloat(formData.current_bid || formData.starting_price),
      min_increment: formData.min_increment ? parseFloat(formData.min_increment) : null,
      end_time: new Date(formData.end_time).toISOString()
    };

    try {
      const url = editingAuction 
        ? `${BACKEND_URL}/api/admin/auctions/${editingAuction.id}`
        : `${BACKEND_URL}/api/admin/auctions`;
      
      const response = await fetch(url, {
        method: editingAuction ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(auctionData)
      });

      if (response.ok) {
        setShowModal(false);
        setEditingAuction(null);
        resetForm();
        fetchAuctions();
      }
    } catch (error) {
      console.error('Failed to save auction:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this auction?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/auctions/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchAuctions();
      }
    } catch (error) {
      console.error('Failed to delete auction:', error);
    }
  };

  const handleCloseAuction = async (auctionId) => {
    if (!window.confirm('Are you sure you want to close this auction?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/auctions/${auctionId}/close`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        fetchAuctions();
      }
    } catch (error) {
      console.error('Failed to close auction:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      product_id: '',
      starting_price: '',
      current_bid: '',
      min_increment: '',
      end_time: '',
      status: 'active'
    });
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Auctions</h1>
            <p className="text-gray-600 dark:text-gray-400">Manage product auctions</p>
          </div>
          <button
            onClick={() => {
              setEditingAuction(null);
              resetForm();
              setShowModal(true);
            }}
            className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-red-700"
          >
            <Plus size={20} />
            Create Auction
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Product</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Starting Price</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Current Bid</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">End Time</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Status</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-500">Loading...</td>
                  </tr>
                ) : auctions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-500">No auctions found</td>
                  </tr>
                ) : (
                  auctions.map((auction) => (
                    <tr key={auction.id} className="border-t dark:border-gray-700">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {auction.product?.image_url && (
                            <img src={auction.product.image_url} alt={auction.product.name} className="w-12 h-12 object-cover rounded" />
                          )}
                          <span className="font-medium text-gray-900 dark:text-white">
                            {auction.product?.name || 'Unknown Product'}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">₹{auction.starting_price}</td>
                      <td className="py-3 px-4 text-green-600 font-medium">₹{auction.current_bid}</td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                        {new Date(auction.end_time).toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(auction.status)}`}>
                          {auction.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          {auction.status === 'active' && (
                            <button
                              onClick={() => handleCloseAuction(auction.id)}
                              className="px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                            >
                              Close
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(auction.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Create Auction</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Product</label>
                  <select
                    required
                    value={formData.product_id}
                    onChange={(e) => setFormData({...formData, product_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select a product</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} - ₹{product.price}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Starting Price (₹)</label>
                    <input
                      type="number"
                      required
                      step="0.01"
                      value={formData.starting_price}
                      onChange={(e) => setFormData({...formData, starting_price: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Min Increment (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.min_increment}
                      onChange={(e) => setFormData({...formData, min_increment: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Time</label>
                  <input
                    type="datetime-local"
                    required
                    value={formData.end_time}
                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingAuction(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700 dark:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Auctions;